import { isEqual } from 'lodash-es';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '@shared/components/AuthProvider';
import { AnalyticsContext } from '@shared/core/analytics';
import { createContext } from '@shared/utils/createContext';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

import { FeatureFlags } from '../featureFlags.constants';
import { useFeatureFlagsContext } from '../featureFlags.provider';
import { RemoteFeatureFlagKeys, RemoteFeatureFlagOptions } from '../featureFlags.types';
import { createOneTimePerfLogger, createPerfLogger, logEvent, parseError, usePerfLogger } from './remoteFeatureFlags.logger';
import { RemoteFeatureFlag, RemoteFeatureFlagService, UserInfo } from './remoteFeatureFlags.types';
import { useAmplitudeExperiment } from './useAmplitudeExperiment';
import { BuildExperimentUserInput, buildExperimentUser } from './amplitude.utils';
import { getLocalStorage } from '@shared/core/storage';
import { useUserConsent } from '@shared/utils/userConsent';
import globalWindow from '@shared/core/globals';
import type { Variants } from '@amplitude/experiment-js-client';

export interface InternalRemoteFeatureFlagsContextType {
  loading: boolean;
  getFeatureFlagValue: <TFeatureName extends RemoteFeatureFlagKeys>(
    key: TFeatureName,
    options?: RemoteFeatureFlagOptions,
  ) => { value?: FeatureFlags[TFeatureName]['supportedValues'][number]; payload?: unknown };
}

const [InternalRemoteFeatureFlagsProvider, useInternalRemoteFeatureFlagsContext, InternalRemoteFeatureFlagsContext] = createContext<InternalRemoteFeatureFlagsContextType>({
  name: 'RemoteFeatureFlagProvider',
});

enum RemoteFeatureFlagProviderState {
  Loading,
  Ready,
}

const logFirstLoadPerf = createOneTimePerfLogger('FirstLoad');

function isSupportedFeatureValue<TFeatureName extends RemoteFeatureFlagKeys>(
  value: string,
  supportedValues: FeatureFlags[TFeatureName]['supportedValues'],
): value is FeatureFlags[TFeatureName]['supportedValues'][number] {
  return Array.isArray(supportedValues) && (supportedValues as string[]).includes(value.toLowerCase());
}

const getForceHotelBlockEnabled = () => {
  const localStorage = getLocalStorage();
  const ajsUserTraits = localStorage.getItem('ajs_user_traits') || '';
  let isForceHotelBlockEnabled = false;
  if (ajsUserTraits) {
    try {
      const ajsUserTraitsObject = JSON.parse(ajsUserTraits);
      if (ajsUserTraitsObject?.forceHotelBlockEnabled) {
        isForceHotelBlockEnabled = ajsUserTraitsObject.forceHotelBlockEnabled === true;
        logEvent('GetForceHotelBlockEnabled', { status: 'success' });
      }
    } catch (err) {
      logEvent('GetForceHotelBlockEnabled', { status: 'error', error: parseError(err) });
    }
  }
  return isForceHotelBlockEnabled;
};

export interface RemoteFeatureFlagProviderParams {
  remoteFeatureFlagService?: RemoteFeatureFlagService;
  initialAmplitudeFeatureFlagVariants?: Variants;
}

export const RemoteFeatureFlagsProvider: React.FC<RemoteFeatureFlagProviderParams> = ({ children, remoteFeatureFlagService, initialAmplitudeFeatureFlagVariants }) => {
  /**
   * Check for window.__INITIAL_AMPLITUDE_FLAGS__ BEFORE calling useAmplitudeExperiment
   * because the hook will delete the global after reading it.
   *
   * On server: initialAmplitudeFeatureFlagVariants prop contains SSR-fetched variants
   * On client: window.__INITIAL_AMPLITUDE_FLAGS__ contains SSR-embedded variants (prop is undefined)
   */
  const hadWindowGlobalOnInit = useRef(!!globalWindow.__INITIAL_AMPLITUDE_FLAGS__);

  const amplitudeService = useAmplitudeExperiment(initialAmplitudeFeatureFlagVariants);
  const featureFlagService = remoteFeatureFlagService ?? amplitudeService;

  // Start in Ready state if we have SSR-fetched variants (from either prop or window global)
  const hasInitialVariants = initialAmplitudeFeatureFlagVariants || hadWindowGlobalOnInit.current;
  const initialState = hasInitialVariants ? RemoteFeatureFlagProviderState.Ready : RemoteFeatureFlagProviderState.Loading;
  const [state, setState] = useState<RemoteFeatureFlagProviderState>(initialState);

  const auth = useAuth();
  const authUserId = auth.currentUser.profile?.activeAlias?.auth0Id;
  const authHasInitialized = auth.hasInitializedOnce;
  usePerfLogger({ name: 'Dependency_Auth', isLoading: !authHasInitialized });

  const { anonymousId } = useContext(AnalyticsContext);
  usePerfLogger({ name: 'Dependency_SegmentUser', isLoading: !anonymousId });

  const { loading: eventInfoLoading, eventInfo } = useEventInfo();
  usePerfLogger({ name: 'Dependency_EventInfo', isLoading: eventInfoLoading });

  const { getUserConsent, loadingUserConsent } = useUserConsent();
  usePerfLogger({ name: 'Dependency_UserConsent', isLoading: loadingUserConsent });

  const lastFetchedUserProfile = useRef<UserInfo>({});

  useEffect(() => {
    // Skip the loading state if we have SSR variants until dependencies are ready
    // This prevents returning default values during SSR when we already have correct values
    if (!anonymousId || !authHasInitialized || eventInfoLoading || loadingUserConsent) {
      return;
    }

    const fetchRemoteFeatureFlags = async () => {
      const forceHotelBlockEnabled = getForceHotelBlockEnabled();

      const userInfoToFetch: BuildExperimentUserInput = {
        userId: authUserId,
        deviceId: anonymousId,
        eventFirebaseId: eventInfo?.eventFirebaseId,
        eventId: eventInfo?.eventId,
        userProperties: {
          forceHotelBlockEnabled,
        },
      };
      if (!isEqual(lastFetchedUserProfile.current, userInfoToFetch)) {
        lastFetchedUserProfile.current = userInfoToFetch;

        const trackFetchUserTime = createPerfLogger('FetchUser');
        try {
          const experimentUser = buildExperimentUser(userInfoToFetch);
          await featureFlagService.fetchUser(experimentUser);
          logEvent('FetchUser', { status: 'success', durationMs: trackFetchUserTime(), user: userInfoToFetch });
        } catch (err) {
          logEvent('FetchUser', { status: 'error', durationMs: trackFetchUserTime(), user: userInfoToFetch, error: parseError(err) });
        }

        logFirstLoadPerf();
      }
      setState(RemoteFeatureFlagProviderState.Ready);
    };

    fetchRemoteFeatureFlags();
  }, [authUserId, authHasInitialized, featureFlagService, anonymousId, eventInfoLoading, eventInfo, loadingUserConsent, hasInitialVariants]);

  const { features } = useFeatureFlagsContext();

  const getFeatureFlagValue = useEventCallback(<TFeatureName extends RemoteFeatureFlagKeys>(featureName: TFeatureName, options?: RemoteFeatureFlagOptions) => {
    // 1. Check URL Query Parameter
    const { defaultValue, value: overriddenFeatureValue, supportedValues, key: featureKey, consentValueForTrue } = features[featureName];
    if (overriddenFeatureValue) {
      return { value: overriddenFeatureValue };
    }

    // 2. Check Event-Level User Consent
    const consentKey = options?.consentKey;
    if (consentKey) {
      const { payload } = options.consentFeatureUsesPayload ? featureFlagService.getFeatureValue(featureKey) : { payload: undefined };
      const consentValue = getUserConsent(consentKey);
      if (consentValue === true) {
        const valueForTrue = consentValueForTrue ?? supportedValues[0];
        return { value: valueForTrue, payload };
      } else if (consentValue === false) {
        const fallbackValue = consentValueForTrue ? supportedValues.find(value => value !== consentValueForTrue) : supportedValues[1];
        return { value: fallbackValue, payload };
      }
    }

    // 3. RemoteConfig (Amplitude) Evaluation
    const { payload, value } = featureFlagService.getFeatureValue(featureKey);
    if (value && isSupportedFeatureValue(value, supportedValues)) {
      featureFlagService.trackExposure(featureKey);
      return { payload, value };
    }

    if (value && !isSupportedFeatureValue(value, supportedValues)) {
      logEvent('UnsupportedFeatureValue', { featureName, value, supportedValues, hasPayload: !!payload, payload });
    }

    return { payload, value: defaultValue };
  });

  return (
    <InternalRemoteFeatureFlagsProvider
      value={{
        loading: state === RemoteFeatureFlagProviderState.Loading,
        getFeatureFlagValue,
      }}
    >
      {children}
    </InternalRemoteFeatureFlagsProvider>
  );
};

export function useRemoteFeatureFlag<TFeatureName extends RemoteFeatureFlagKeys>(featureName?: TFeatureName, options?: RemoteFeatureFlagOptions): RemoteFeatureFlag<TFeatureName> {
  const { loading, getFeatureFlagValue } = useInternalRemoteFeatureFlagsContext();

  const { features } = useFeatureFlagsContext();
  const defaultFeatureValue = featureName && features[featureName].defaultValue;

  // Use primitive values instead of options reference to avoid re-triggering trackExposure
  // when callers pass inline objects like { consentKey: 'x' } that create new refs every render
  const consentKey = options?.consentKey;
  const skip = options?.skip;
  const consentFeatureUsesPayload = options?.consentFeatureUsesPayload;

  const remoteFeatureFlag = useMemo((): RemoteFeatureFlag<TFeatureName> => {
    if (skip) {
      return {
        loading: false,
        value: defaultFeatureValue,
      };
    }

    if (loading || !featureName) {
      return {
        loading: true,
        value: defaultFeatureValue,
      };
    }

    return {
      loading: false,
      ...getFeatureFlagValue(featureName, { consentKey, skip, consentFeatureUsesPayload }),
    };
  }, [loading, featureName, getFeatureFlagValue, defaultFeatureValue, consentKey, skip, consentFeatureUsesPayload]);

  return remoteFeatureFlag;
}

export { InternalRemoteFeatureFlagsContext };
